// FormulaEditor yardımcı bileşenleri (dosya ≤600 satır kuralı için ayrıldı):
// HighlightedExpression (bilinmeyen kod altı çizili), ConvertPicker (CEVIR satır içi seçici —
// gösterge → kaynak birim otomatik/salt okunur → ulaşılabilir hedef → canlı önizleme → Ekle),
// SupportedConversions (katlanabilir "Desteklenen çevrimler" tablosu, aile gruplu).

import { useState } from "react";
import {
  KPI_UNIT_CONVERSIONS,
  KPI_UNIT_FAMILIES,
  reachableUnits,
  unitDisplayName,
  unitFamilyOf,
} from "@mahir/shared";
import { buildConvertSnippet, convertPreview } from "../../lib/kpi-set-editor";
import { SearchSelect } from "../common/SearchSelect";
import { btnSmall, inputStyle, monoStyle } from "./kpi-form-styles";

export function HighlightedExpression({
  expression,
  unknown,
  scopeText,
}: {
  expression: string;
  unknown: string[];
  scopeText: string;
}): JSX.Element {
  const parts = expression.split(/([a-z][a-z0-9_]*)/g);
  return (
    <span style={monoStyle}>
      {parts.map((p, i) =>
        unknown.includes(p) ? (
          <span
            key={i}
            title={scopeText}
            style={{
              textDecoration: "underline wavy var(--color-error)",
              color: "var(--color-error)",
            }}
          >
            {p}
          </span>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </span>
  );
}

const ALL_TABLE_UNITS: readonly string[] = KPI_UNIT_FAMILIES.flatMap((f) => f.units);

const selectSmall = { ...inputStyle, minHeight: 32, padding: "4px 6px", fontSize: "0.8rem" };

interface ConvertPickerProps {
  codes: readonly string[];
  unitsByCode: ReadonlyMap<string, string | null> | undefined;
  labelsByCode?: ReadonlyMap<string, string> | undefined;
  onInsert: (snippet: string) => void;
  onCancel: () => void;
}

/** CEVIR popover'ı: gösterge → kaynak birim → ulaşılabilir hedef → önizleme → Ekle. */
export function ConvertPicker({
  codes,
  unitsByCode,
  labelsByCode,
  onInsert,
  onCancel,
}: ConvertPickerProps): JSX.Element {
  const [code, setCode] = useState<string>(codes[0] ?? "");
  const [manualFrom, setManualFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const indicatorUnit = code ? (unitsByCode?.get(code) ?? null) : null;
  const indicatorUnitKnown =
    indicatorUnit !== null &&
    indicatorUnit.trim() !== "" &&
    reachableUnits(indicatorUnit).length > 0;
  const from = indicatorUnitKnown ? indicatorUnit : manualFrom;
  const targets = from ? reachableUnits(from) : [];
  const effectiveTo = targets.includes(to) ? to : (targets[0] ?? "");
  const preview = from && effectiveTo ? convertPreview(from, effectiveTo) : null;
  const unitNotConvertible =
    indicatorUnit !== null && indicatorUnit.trim() !== "" && !indicatorUnitKnown;

  return (
    <div
      role="group"
      aria-label="Birim çevir (CEVIR) ekle"
      className="rounded-lg p-2 mt-2"
      style={{
        border: "1px dashed var(--color-primary)",
        background: "color-mix(in srgb, var(--color-primary) 4%, white)",
        display: "grid",
        gap: 6,
      }}
    >
      {/* dar çekmecede tek sütun — 3 sütun 340 px'te sığmıyor */}
      <div className="grid gap-2">
        <label className="grid gap-1 text-xs" style={{ color: "var(--color-text-2)" }}>
          Gösterge
          <SearchSelect
            ariaLabel="Çevrilecek gösterge"
            size="sm"
            value={code}
            searchPlaceholder="Gösterge adı ya da kodu…"
            options={codes.map((c) => ({
              value: c,
              label: labelsByCode?.get(c) ?? c,
              code: labelsByCode?.get(c) ? c : undefined,
              hint: unitsByCode?.get(c) ?? undefined,
            }))}
            onChange={(v) => {
              setCode(v);
              setTo("");
            }}
          />
        </label>
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
          <label className="grid gap-1 text-xs" style={{ color: "var(--color-text-2)" }}>
            {indicatorUnitKnown ? "Kaynak birim (göstergenin birimi)" : "Kaynak birim"}
            {indicatorUnitKnown ? (
              <input
                aria-label="Kaynak birim (göstergenin birimi)"
                readOnly
                style={{ ...selectSmall, background: "var(--color-bg)" }}
                value={unitDisplayName(indicatorUnit)}
              />
            ) : (
              <select
                aria-label="Kaynak birim"
                style={selectSmall}
                value={manualFrom}
                onChange={(e) => {
                  setManualFrom(e.target.value);
                  setTo("");
                }}
              >
                <option value="">Seçin…</option>
                {ALL_TABLE_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            )}
          </label>
          <label className="grid gap-1 text-xs" style={{ color: "var(--color-text-2)" }}>
            Hedef birim
            <select
              aria-label="Hedef birim"
              style={selectSmall}
              value={effectiveTo}
              disabled={targets.length === 0}
              onChange={(e) => setTo(e.target.value)}
            >
              {targets.length === 0 ? <option value="">—</option> : null}
              {targets.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      {unitNotConvertible ? (
        <div className="text-xs" style={{ color: "var(--color-warning-text)" }}>
          Göstergenin birimi ({indicatorUnit}) çevrim tablosunda yok — kaynak birimi elle seçin ya
          da göstergeyi değiştirin.
        </div>
      ) : null}
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className="text-xs"
          style={{ ...monoStyle, color: "var(--color-text-2)" }}
          aria-live="polite"
        >
          {preview ?? "Önizleme için kaynak ve hedef birim seçin."}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" style={btnSmall} onClick={onCancel}>
          Vazgeç
        </button>
        <button
          type="button"
          style={{
            ...btnSmall,
            borderColor: "var(--color-primary)",
            color: "var(--color-primary)",
          }}
          disabled={!code || !from || !effectiveTo}
          onClick={() =>
            onInsert(
              buildConvertSnippet({
                code,
                to: effectiveTo,
                from,
                fromIndicatorUnit: indicatorUnitKnown,
              }),
            )
          }
        >
          Ekle
        </button>
      </div>
    </div>
  );
}

/** Katlanabilir "Desteklenen çevrimler" tablosu — tohum çiftleri + türetilen tersler, aile gruplu. */
export function SupportedConversions(): JSX.Element {
  const fmt = (n: number) => n.toLocaleString("tr-TR", { maximumSignificantDigits: 6 });
  return (
    <details className="text-xs" style={{ color: "var(--color-text-2)" }}>
      <summary
        style={{
          cursor: "pointer",
          color: "var(--color-primary)",
          minHeight: 32,
          lineHeight: "32px",
        }}
      >
        Desteklenen çevrimler
      </summary>
      <table className="w-full" style={{ borderCollapse: "collapse", marginTop: 4 }}>
        <thead>
          <tr style={{ color: "var(--color-text-3)", textAlign: "left" }}>
            <th style={{ padding: "2px 4px", fontWeight: 600 }}>Grup</th>
            <th style={{ padding: "2px 4px", fontWeight: 600 }}>Çevrim</th>
          </tr>
        </thead>
        <tbody>
          {KPI_UNIT_FAMILIES.map((fam) => {
            const rows = KPI_UNIT_CONVERSIONS.filter((c) => unitFamilyOf(c.from) === fam.key);
            return rows.map((c, i) => (
              <tr
                key={`${fam.key}-${c.from}-${c.to}`}
                style={{ borderTop: "1px solid var(--color-border)" }}
              >
                {i === 0 ? (
                  <td
                    rowSpan={rows.length}
                    style={{ padding: "2px 4px", verticalAlign: "top", fontWeight: 600 }}
                  >
                    {fam.label}
                  </td>
                ) : null}
                <td style={{ ...monoStyle, padding: "2px 4px" }}>
                  1 {c.from} = {fmt(c.factor)} {c.to}
                </td>
              </tr>
            ));
          })}
        </tbody>
      </table>
      <div style={{ marginTop: 4 }}>
        Aynı gruptaki birimler arasında çok adımlı çevrim de yapılır (kWh → MWh → TJ). Para
        birimleri (TL, USD, EUR) çevrilmez — kur verisi tutulmaz.
      </div>
    </details>
  );
}
